import React from "react";

import { ROI_REQUEST_SELECTOR_SECTIONS } from "./roi-request-create.dataset";
import {
  RoiRequestCreateStep,
  RoiRequestLifecycleState,
  RoiRequestSectionSelection,
  RoiSelectorRow,
  RoiSelectorSection,
  RoiRequestState,
} from "./roi-request-create.types";

const MIN_DATE_YEAR = 1900;
const MAX_DATE_YEAR = 2100;
const AMERICAN_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const normalizeDateInput = (value: string) => {
  const digitsOnlyValue = value.replace(/\D/g, "").slice(0, 8);
  const month = digitsOnlyValue.slice(0, 2);
  const day = digitsOnlyValue.slice(2, 4);
  const year = digitsOnlyValue.slice(4, 8);

  if (digitsOnlyValue.length <= 2) {
    return month;
  }

  if (digitsOnlyValue.length <= 4) {
    return `${month}/${day}`;
  }

  return `${month}/${day}/${year}`;
};

const parseDatePartsToTimestamp = (
  year: number,
  month: number,
  day: number,
) => {
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date.getTime();
};

const parseAmericanDateInput = (value: string) => {
  const matchedDate = value.match(AMERICAN_DATE_PATTERN);

  if (!matchedDate) {
    return null;
  }

  const [, monthPart, dayPart, yearPart] = matchedDate;
  const year = Number.parseInt(yearPart, 10);
  const month = Number.parseInt(monthPart, 10);
  const day = Number.parseInt(dayPart, 10);

  if (year < MIN_DATE_YEAR || year > MAX_DATE_YEAR) {
    return null;
  }

  return parseDatePartsToTimestamp(year, month, day);
};

const getAmericanDateValidationMessage = (value: string) => {
  if (value.trim().length === 0) {
    return null;
  }

  const matchedDate = value.match(AMERICAN_DATE_PATTERN);

  if (!matchedDate) {
    return "Use MM/DD/YYYY";
  }

  const [, monthPart, dayPart, yearPart] = matchedDate;
  const year = Number.parseInt(yearPart, 10);
  const month = Number.parseInt(monthPart, 10);
  const day = Number.parseInt(dayPart, 10);

  if (month < 1 || month > 12) {
    return "Invalid month";
  }

  if (day < 1 || day > 31) {
    return "Invalid day";
  }

  if (year < MIN_DATE_YEAR || year > MAX_DATE_YEAR) {
    return "Invalid year";
  }

  if (parseDatePartsToTimestamp(year, month, day) === null) {
    return "Enter a real date";
  }

  return null;
};

const parseDatasetDateValue = (value: RoiSelectorRow[keyof RoiSelectorRow]) => {
  if (typeof value !== "string") {
    return null;
  }

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (isoDateMatch) {
    const [, yearPart, monthPart, dayPart] = isoDateMatch;

    return parseDatePartsToTimestamp(
      Number.parseInt(yearPart, 10),
      Number.parseInt(monthPart, 10),
      Number.parseInt(dayPart, 10),
    );
  }

  return parseAmericanDateInput(value);
};

const isRowWithinDateRange = ({
  row,
  fromTimestamp,
  toTimestamp,
  dateFilterKeys,
}: {
  row: RoiSelectorRow;
  fromTimestamp: number | null;
  toTimestamp: number | null;
  dateFilterKeys?: string[];
}) => {
  if (!dateFilterKeys || dateFilterKeys.length === 0) {
    return true;
  }

  const rowTimestamps = dateFilterKeys
    .map((key) => parseDatasetDateValue(row[key]))
    .filter((value): value is number => value !== null);

  if (rowTimestamps.length === 0) {
    return true;
  }

  return rowTimestamps.some((timestamp) => {
    if (fromTimestamp !== null && timestamp < fromTimestamp) {
      return false;
    }

    if (toTimestamp !== null && timestamp > toTimestamp) {
      return false;
    }

    return true;
  });
};

const filterSectionsByDateRange = ({
  sections,
  fromTimestamp,
  toTimestamp,
}: {
  sections: RoiSelectorSection[];
  fromTimestamp: number | null;
  toTimestamp: number | null;
}) =>
  sections.map((section) => ({
    ...section,
    table: {
      ...section.table,
      rows: section.table.rows.filter((row) =>
        isRowWithinDateRange({
          row,
          fromTimestamp,
          toTimestamp,
          dateFilterKeys: section.date_filter_keys,
        }),
      ),
    },
  }));

const getDefaultActiveSectionId = () =>
  ROI_REQUEST_SELECTOR_SECTIONS.find(
    (section) => section.section_id === "flowsheets",
  )?.section_id ??
  ROI_REQUEST_SELECTOR_SECTIONS.find(
    (section) => !section.locked && !section.disabled,
  )?.section_id ??
  ROI_REQUEST_SELECTOR_SECTIONS[0]?.section_id ??
  "";

const getInitialActiveSectionId = (
  initialRequestState?: RoiRequestState | null,
) =>
  initialRequestState?.data.find((sectionSelection) => {
    if (sectionSelection.selection.length === 0) {
      return false;
    }

    const matchedSection = ROI_REQUEST_SELECTOR_SECTIONS.find(
      (section) => section.section_id === sectionSelection.section_id,
    );

    return Boolean(
      matchedSection && !matchedSection.locked && !matchedSection.disabled,
    );
  })?.section_id ?? getDefaultActiveSectionId();

const normalizeSelections = (
  selections: RoiRequestSectionSelection[],
): RoiRequestSectionSelection[] =>
  selections
    .filter((section) => section.selection.length > 0)
    .sort((leftSection, rightSection) => {
      const leftIndex = ROI_REQUEST_SELECTOR_SECTIONS.findIndex(
        (section) => section.section_id === leftSection.section_id,
      );
      const rightIndex = ROI_REQUEST_SELECTOR_SECTIONS.findIndex(
        (section) => section.section_id === rightSection.section_id,
      );

      return leftIndex - rightIndex;
    });

const getTotalSelectedItemsCount = (
  selections: RoiRequestSectionSelection[],
): number =>
  selections.reduce((total, section) => total + section.selection.length, 0);

const createRequestState = ({
  requestId,
  lifecycleState,
  selections,
}: {
  requestId: number;
  lifecycleState: RoiRequestLifecycleState;
  selections: RoiRequestSectionSelection[];
}): RoiRequestState => {
  const normalizedSelections = normalizeSelections(selections);

  return {
    request_id: requestId,
    state: lifecycleState,
    total_selected_items_count:
      getTotalSelectedItemsCount(normalizedSelections),
    data: normalizedSelections,
  };
};

export const useRoiRequestCreate = ({
  mode = "create",
  initialRequestState = null,
}: {
  mode?: "create" | "edit";
  initialRequestState?: RoiRequestState | null;
} = {}) => {
  const getInitialStep = React.useCallback(
    (): RoiRequestCreateStep =>
      mode === "edit" ? "create-details" : "create-id",
    [mode],
  );
  const getInitialRequestState = React.useCallback(
    () =>
      initialRequestState
        ? createRequestState({
            requestId: initialRequestState.request_id,
            lifecycleState: initialRequestState.state,
            selections: initialRequestState.data,
          })
        : createRequestState({
            requestId: 0,
            lifecycleState: "DRAFT",
            selections: [],
          }),
    [initialRequestState],
  );
  const [currentStep, setCurrentStep] =
    React.useState<RoiRequestCreateStep>(getInitialStep);
  const [activeSectionId, setActiveSectionIdState] = React.useState<string>(
    getInitialActiveSectionId(initialRequestState),
  );
  const [requestIdInput, setRequestIdInput] = React.useState(
    initialRequestState ? String(initialRequestState.request_id) : "",
  );
  const [searchTerm, setSearchTermState] = React.useState("");
  const [dateRangeFromInput, setDateRangeFromInput] = React.useState("");
  const [dateRangeToInput, setDateRangeToInput] = React.useState("");
  const [isDateRangeFromTouched, setIsDateRangeFromTouched] =
    React.useState(false);
  const [isDateRangeToTouched, setIsDateRangeToTouched] = React.useState(false);
  const [requestState, setRequestState] = React.useState<RoiRequestState>(
    getInitialRequestState,
  );
  const dateRangeFromTimestamp = React.useMemo(
    () => parseAmericanDateInput(dateRangeFromInput),
    [dateRangeFromInput],
  );
  const dateRangeToTimestamp = React.useMemo(
    () => parseAmericanDateInput(dateRangeToInput),
    [dateRangeToInput],
  );
  const dateRangeOrderValidationMessage =
    dateRangeFromTimestamp !== null &&
    dateRangeToTimestamp !== null &&
    dateRangeFromTimestamp > dateRangeToTimestamp
      ? "From must be before To"
      : null;
  const dateRangeFromValidationMessage =
    (isDateRangeFromTouched
      ? getAmericanDateValidationMessage(dateRangeFromInput)
      : null) ??
    (isDateRangeFromTouched ? dateRangeOrderValidationMessage : null);
  const dateRangeToValidationMessage =
    (isDateRangeToTouched
      ? getAmericanDateValidationMessage(dateRangeToInput)
      : null) ??
    (isDateRangeToTouched ? dateRangeOrderValidationMessage : null);
  const filteredSections = React.useMemo(
    () =>
      filterSectionsByDateRange({
        sections: ROI_REQUEST_SELECTOR_SECTIONS,
        fromTimestamp: dateRangeFromTimestamp,
        toTimestamp: dateRangeToTimestamp,
      }),
    [dateRangeFromTimestamp, dateRangeToTimestamp],
  );

  const openFlow = React.useCallback(() => {
    setCurrentStep(getInitialStep());
    setActiveSectionIdState(getInitialActiveSectionId(initialRequestState));
    setRequestIdInput(
      initialRequestState ? String(initialRequestState.request_id) : "",
    );
    setSearchTermState("");
    setDateRangeFromInput("");
    setDateRangeToInput("");
    setIsDateRangeFromTouched(false);
    setIsDateRangeToTouched(false);
    setRequestState(getInitialRequestState());
  }, [getInitialRequestState, getInitialStep, initialRequestState]);

  const exitFlow = React.useCallback(() => {
    openFlow();
  }, [openFlow]);

  const goBack = React.useCallback(() => {
    setCurrentStep((prevStep) =>
      prevStep === "create-details" ? "create-id" : prevStep,
    );
  }, []);

  const continueFromRequestId = React.useCallback(() => {
    const normalizedRequestId = Number.parseInt(requestIdInput, 10);

    if (!Number.isFinite(normalizedRequestId) || normalizedRequestId <= 0) {
      return false;
    }

    setRequestState((prevState) =>
      createRequestState({
        requestId: normalizedRequestId,
        lifecycleState: prevState.state,
        selections: prevState.data,
      }),
    );
    setCurrentStep("create-details");

    return true;
  }, [requestIdInput]);

  const setRequestId = React.useCallback((nextValue: string) => {
    const digitsOnlyValue = nextValue.replace(/\D/g, "");
    setRequestIdInput(digitsOnlyValue);
  }, []);

  const setSearchTerm = React.useCallback((nextValue: string) => {
    setSearchTermState(nextValue);
  }, []);

  const setDateRangeFrom = React.useCallback((nextValue: string) => {
    setDateRangeFromInput(normalizeDateInput(nextValue));
    setIsDateRangeFromTouched(false);
  }, []);

  const setDateRangeTo = React.useCallback((nextValue: string) => {
    setDateRangeToInput(normalizeDateInput(nextValue));
    setIsDateRangeToTouched(false);
  }, []);

  const clearDateRangeFrom = React.useCallback(() => {
    setDateRangeFromInput("");
    setIsDateRangeFromTouched(false);
  }, []);

  const clearDateRangeTo = React.useCallback(() => {
    setDateRangeToInput("");
    setIsDateRangeToTouched(false);
  }, []);

  const markDateRangeFromTouched = React.useCallback(() => {
    setIsDateRangeFromTouched(true);
  }, []);

  const markDateRangeToTouched = React.useCallback(() => {
    setIsDateRangeToTouched(true);
  }, []);

  const setActiveSection = React.useCallback((sectionId: string) => {
    const targetSection = ROI_REQUEST_SELECTOR_SECTIONS.find(
      (section) => section.section_id === sectionId,
    );

    if (!targetSection || targetSection.locked || targetSection.disabled) {
      return;
    }

    setActiveSectionIdState(sectionId);
  }, []);

  const toggleRowSelection = React.useCallback(
    (sectionId: string, rowId: string) => {
      setRequestState((prevState) => {
        const existingSection = prevState.data.find(
          (section) => section.section_id === sectionId,
        );
        const existingSelection = existingSection?.selection ?? [];
        const nextSelection = existingSelection.includes(rowId)
          ? existingSelection.filter((selectedRowId) => selectedRowId !== rowId)
          : [...existingSelection, rowId];
        const nextSections = prevState.data.filter(
          (section) => section.section_id !== sectionId,
        );

        if (nextSelection.length > 0) {
          nextSections.push({
            section_id: sectionId,
            selection: nextSelection,
          });
        }

        return createRequestState({
          requestId: prevState.request_id,
          lifecycleState: prevState.state,
          selections: nextSections,
        });
      });
    },
    [],
  );

  const toggleAllRowsInSection = React.useCallback(
    (sectionId: string, rowIds: string[]) => {
      setRequestState((prevState) => {
        if (rowIds.length === 0) {
          return prevState;
        }

        const existingSection = prevState.data.find(
          (section) => section.section_id === sectionId,
        );
        const existingSelection = existingSection?.selection ?? [];
        const areAllRowsSelected = rowIds.every((rowId) =>
          existingSelection.includes(rowId),
        );
        const nextSelection = areAllRowsSelected
          ? existingSelection.filter(
              (selectedRowId) => !rowIds.includes(selectedRowId),
            )
          : Array.from(new Set([...existingSelection, ...rowIds]));
        const nextSections = prevState.data.filter(
          (section) => section.section_id !== sectionId,
        );

        if (nextSelection.length > 0) {
          nextSections.push({
            section_id: sectionId,
            selection: nextSelection,
          });
        }

        return createRequestState({
          requestId: prevState.request_id,
          lifecycleState: prevState.state,
          selections: nextSections,
        });
      });
    },
    [],
  );

  const getSectionSelection = React.useCallback(
    (sectionId: string) =>
      requestState.data.find((section) => section.section_id === sectionId)
        ?.selection ?? [],
    [requestState.data],
  );

  const isSectionSelected = React.useCallback(
    (sectionId: string) => getSectionSelection(sectionId).length > 0,
    [getSectionSelection],
  );

  const getActiveSection = React.useCallback(
    () =>
      filteredSections.find(
        (section) => section.section_id === activeSectionId,
      ) ?? filteredSections[0],
    [activeSectionId, filteredSections],
  );

  const getSelectableRowsForActiveSection = React.useCallback(
    () => getActiveSection()?.table.rows ?? [],
    [getActiveSection],
  );

  const isCompleteAllowed = requestState.total_selected_items_count > 0;
  const isSaveDraftAllowed = requestState.total_selected_items_count > 0;

  React.useEffect(() => {
    const availableRowIdsBySection = new Map(
      filteredSections.map((section) => [
        section.section_id,
        new Set(section.table.rows.map((row) => row.data_element_id)),
      ]),
    );

    setRequestState((prevState) => {
      let hasChanges = false;
      const nextSelections = prevState.data.flatMap((sectionSelection) => {
        const availableRowIds = availableRowIdsBySection.get(
          sectionSelection.section_id,
        );

        if (!availableRowIds) {
          return [sectionSelection];
        }

        const nextSelection = sectionSelection.selection.filter((rowId) =>
          availableRowIds.has(rowId),
        );

        if (nextSelection.length !== sectionSelection.selection.length) {
          hasChanges = true;
        }

        return nextSelection.length > 0
          ? [
              {
                ...sectionSelection,
                selection: nextSelection,
              },
            ]
          : [];
      });

      if (!hasChanges) {
        return prevState;
      }

      return createRequestState({
        requestId: prevState.request_id,
        lifecycleState: prevState.state,
        selections: nextSelections,
      });
    });
  }, [filteredSections]);

  const setRequestStateDraft = React.useCallback(() => {
    const nextState = createRequestState({
      requestId: requestState.request_id,
      lifecycleState: "DRAFT",
      selections: requestState.data,
    });

    setRequestState(nextState);

    return nextState;
  }, [requestState]);

  const setRequestStateCompleted = React.useCallback(() => {
    const nextState = createRequestState({
      requestId: requestState.request_id,
      lifecycleState: "COMPLETED",
      selections: requestState.data,
    });

    setRequestState(nextState);

    return nextState;
  }, [requestState]);

  return {
    sections: filteredSections,
    currentStep,
    activeSectionId,
    requestIdInput,
    searchTerm,
    dateRangeFromInput,
    dateRangeToInput,
    dateRangeFromValidationMessage,
    dateRangeToValidationMessage,
    requestState,
    openFlow,
    exitFlow,
    goBack,
    continueFromRequestId,
    setRequestId,
    setSearchTerm,
    setDateRangeFrom,
    setDateRangeTo,
    clearDateRangeFrom,
    clearDateRangeTo,
    markDateRangeFromTouched,
    markDateRangeToTouched,
    setActiveSection,
    toggleRowSelection,
    toggleAllRowsInSection,
    setRequestStateDraft,
    setRequestStateCompleted,
    getTotalSelectedItemsCount: requestState.total_selected_items_count,
    getSectionSelection,
    isSectionSelected,
    isCompleteAllowed,
    isSaveDraftAllowed,
    getActiveSection,
    getSelectableRowsForActiveSection,
  };
};
